/**
 * Analytics Hook
 * Provides a convenient interface for tracking events in components
 */

import { useCallback, useMemo } from 'react';
import { useLocation } from '@/hooks/useLocation';
import { usePostHogSafe } from '../context/PostHogContext';
import {
  trackEvent,
  trackPageView as trackPageViewService,
  captureException as captureExceptionService,
  registerSuperProperties,
  unregisterSuperProperty
} from '../services/analytics';
import { sanitizeUrl } from '../utils/analytics-masking';
import type {
  AnalyticsEvent,
  EventProperties,
  NavigationEventProperties,
  OperatorActionEventProperties,
  SearchFilterEventProperties,
  WorkflowEventProperties,
  ErrorEventProperties
} from '../types/analytics';

/** Return type for useAnalytics hook */
interface UseAnalyticsReturn {
  /** Track a custom event */
  capture: (event: AnalyticsEvent, properties?: EventProperties) => void;
  /** Track a page view event */
  trackPageView: (pageName: string) => void;
  /** Track a navigation event */
  trackNavigation: (event: 'tab_opened' | 'tab_closed' | 'tab_switched' | 'menu_item_clicked', properties?: NavigationEventProperties) => void;
  /** Track an operator action */
  trackAction: (event: AnalyticsEvent, properties?: OperatorActionEventProperties) => void;
  /** Track a search or filter event */
  trackSearch: (event: 'search_performed' | 'filter_applied' | 'filter_cleared', properties?: SearchFilterEventProperties) => void;
  /** Track a workflow event */
  trackWorkflow: (event: 'ai_colleague_invoked' | 'ai_colleague_response_received' | 'report_generated' | 'report_exported', properties?: WorkflowEventProperties) => void;
  /** Track an error event */
  trackError: (event: 'api_error' | 'validation_error' | 'auth_error', properties?: ErrorEventProperties) => void;
  /** Capture an exception */
  captureException: (error: Error, context?: Record<string, unknown>) => void;
  /** Register super properties (included with every event) */
  setSuperProperties: (properties: Record<string, unknown>) => void;
  /** Remove a super property */
  removeSuperProperty: (propertyName: string) => void;
  /** Whether analytics is enabled */
  isEnabled: boolean;
}

/**
 * Hook for tracking analytics events in components
 * Provides typed methods for different event categories
 */
export function useAnalytics(): UseAnalyticsReturn {
  const posthog = usePostHogSafe();
  const location = useLocation();

  const isEnabled = posthog?.isEnabled ?? false;

  // Get sanitized current route
  const currentRoute = useMemo(
    () => sanitizeUrl(location.pathname + location.search),
    [location.pathname, location.search]
  );

  // Generic event capture
  const capture = useCallback(
    (event: AnalyticsEvent, properties?: EventProperties) => {
      if (!isEnabled) return;

      trackEvent(event, {
        ...properties,
        route: currentRoute
      });
    },
    [isEnabled, currentRoute]
  );

  // Page view tracking
  const trackPageView = useCallback(
    (pageName: string) => {
      if (!isEnabled) return;

      trackPageViewService(pageName, currentRoute);
    },
    [isEnabled, currentRoute]
  );

  // Navigation events
  const trackNavigation = useCallback(
    (
      event: 'tab_opened' | 'tab_closed' | 'tab_switched' | 'menu_item_clicked',
      properties?: NavigationEventProperties
    ) => {
      if (!isEnabled) return;

      trackEvent(event, {
        ...properties,
        route: currentRoute
      });
    },
    [isEnabled, currentRoute]
  );

  // Operator action events
  const trackAction = useCallback(
    (event: AnalyticsEvent, properties?: OperatorActionEventProperties) => {
      if (!isEnabled) return;

      trackEvent(event, {
        ...properties,
        route: currentRoute
      });
    },
    [isEnabled, currentRoute]
  );

  // Search and filter events
  const trackSearch = useCallback(
    (
      event: 'search_performed' | 'filter_applied' | 'filter_cleared',
      properties?: SearchFilterEventProperties
    ) => {
      if (!isEnabled) return;

      trackEvent(event, {
        ...properties,
        route: currentRoute
      });
    },
    [isEnabled, currentRoute]
  );

  // Workflow events
  const trackWorkflow = useCallback(
    (
      event: 'ai_colleague_invoked' | 'ai_colleague_response_received' | 'report_generated' | 'report_exported',
      properties?: WorkflowEventProperties
    ) => {
      if (!isEnabled) return;

      trackEvent(event, {
        ...properties,
        route: currentRoute
      });
    },
    [isEnabled, currentRoute]
  );

  // Error events
  const trackError = useCallback(
    (
      event: 'api_error' | 'validation_error' | 'auth_error',
      properties?: ErrorEventProperties
    ) => {
      if (!isEnabled) return;

      trackEvent(event, {
        ...properties,
        route: currentRoute
      });
    },
    [isEnabled, currentRoute]
  );

  // Exception capture
  const captureException = useCallback(
    (error: Error, context?: Record<string, unknown>) => {
      if (!isEnabled) return;

      captureExceptionService(error, {
        ...context,
        route: currentRoute
      });
    },
    [isEnabled, currentRoute]
  );

  // Super properties
  const setSuperProperties = useCallback(
    (properties: Record<string, unknown>) => {
      if (!isEnabled) return;

      registerSuperProperties(properties);
    },
    [isEnabled]
  );

  const removeSuperProperty = useCallback(
    (propertyName: string) => {
      if (!isEnabled) return;

      unregisterSuperProperty(propertyName);
    },
    [isEnabled]
  );

  return {
    capture,
    trackPageView,
    trackNavigation,
    trackAction,
    trackSearch,
    trackWorkflow,
    trackError,
    captureException,
    setSuperProperties,
    removeSuperProperty,
    isEnabled
  };
}

export default useAnalytics;
